import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import type { OrgConfig, OrgFeatures } from '@type/orgConfig';
import { useIdentityAuth } from '@auth/IdentityAuthContext';
import {
  getStoredOrgBranding,
  storeOrgBranding,
  clearStoredOrgBranding,
} from '@utils/themes';

interface OrgConfigContextValue {
  branding: OrgConfig | null;
  features: OrgFeatures | null;
  loading: boolean;
  error: string | null;
}

const OrgConfigContext = createContext<OrgConfigContextValue | undefined>(
  undefined
);

interface ProviderProps {
  orgId: number | null;
  marketId: number | null;
  children: ReactNode;
}

export const OrgConfigProvider: React.FC<ProviderProps> = ({
  orgId,
  marketId,
  children,
}) => {
  // Seed from this org's last-known-good so branding/features are available on first render
  // (avoids the default-styling flash); the fetch below refreshes it.
  const [branding, setBranding] = useState<OrgConfig | null>(() => getStoredOrgBranding(orgId));
  const [loading, setLoading] = useState<boolean>(orgId != null);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken, login, isAuthenticated } = useIdentityAuth();

  // On store/org switch, immediately swap to the new org's last-known-good (or nothing) rather than
  // leaving the previous org's branding on screen while the new config loads. Keyed only on
  // orgId/marketId so it fires once per switch — never on every render.
  useEffect(() => {
    setBranding(getStoredOrgBranding(orgId));
     
  }, [orgId, marketId]);

  useEffect(() => {
    if (orgId == null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const url = `${process.env.REACT_APP_API_DOMAIN}v1/orgs/${orgId}/config${
      marketId != null ? `?marketId=${marketId}` : ''
    }`;
    let cancelled = false;
    (async () => {
      try {
        if (!isAuthenticated) return;
        let token: string | null = null;
        try {
          token = await getAccessToken();
        } catch (e) {
          console.warn('Failed to get Access Identity token:', e);
        }
        if (!token) {
          if (!cancelled) setLoading(false);
          await login();
          return;
        }
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 409) {
          // Org not SMP-enabled — drop this org's stale cached branding so it can't linger.
          clearStoredOrgBranding(orgId);
          if (!cancelled) setBranding(null);
          return;
        }
        if (!res.ok) {
          throw new Error(`Failed to load org config: ${res.status}`);
        }
        const data = (await res.json()) as OrgConfig;
        storeOrgBranding(data);
        if (!cancelled) setBranding(data);
      } catch (e) {
        if (!cancelled)
          setError((e as Error).message ?? 'Failed to load org config');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, marketId, getAccessToken, login, isAuthenticated]);

  return (
    <OrgConfigContext.Provider
      value={{
        branding,
        features: branding?.features ?? null,
        loading,
        error,
      }}
    >
      {children}
    </OrgConfigContext.Provider>
  );
};

export const useOrgConfig = (): OrgConfigContextValue => {
  const ctx = useContext(OrgConfigContext);
  if (!ctx) {
    throw new Error('useOrgConfig must be used within OrgConfigProvider');
  }
  return ctx;
};

export const useOrgConfigOptional = (): OrgConfigContextValue | null => {
  return useContext(OrgConfigContext) ?? null;
};
